import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { useQobuzStore } from '../lib/useQobuz';

const ServicesContext = createContext(null);

export function useServices() {
  const ctx = useContext(ServicesContext);
  if (!ctx) throw new Error('useServices must be used within ServicesProvider');
  return ctx;
}

const STREAMING_SERVICE_IDS = ['qobuz', 'prestomusic', 'spotify', 'deezer', 'soundcloud'];

const DEFAULT_SERVICES = {
  qobuz: { connected: false, active: true, name: "Qobuz" },
  prestomusic: { connected: false, active: false, name: "Presto Music" },
  local: { connected: true, active: true, name: "Local" },
  live_radio: { connected: true, active: true, name: "Live Radio" },
  ella_radio: { connected: true, active: true, name: "Ella Radio" },
  octave_radio: { connected: true, active: true, name: "Octave Radio" },
  spotify: { connected: false, active: false, name: "Spotify" },
  deezer: { connected: false, active: false, name: "Deezer" },
  soundcloud: { connected: false, active: false, name: "SoundCloud" },
};

const DEFAULTS_VERSION = 3;

function loadInitialServices() {
  const defaults = { ...DEFAULT_SERVICES };
  for (const key of Object.keys(defaults)) {
    defaults[key] = { ...defaults[key] };
  }
  try {
    const saved = localStorage.getItem('maestro_services');
    if (saved) {
      const parsed = JSON.parse(saved);
      const savedVersion = parsed._defaults_version || 0;
      for (const key of Object.keys(defaults)) {
        if (parsed[key]) {
          defaults[key].active = parsed[key].active === true;
          defaults[key].connected = parsed[key].connected === true;
        }
      }
      if (savedVersion < DEFAULTS_VERSION) {
        defaults.qobuz.active = true;
        defaults.live_radio.active = true;
        defaults.live_radio.connected = true;
        defaults.ella_radio.active = true;
        defaults.ella_radio.connected = true;
        defaults.octave_radio.active = true;
        defaults.octave_radio.connected = true;
      }
    }
  } catch {}
  const qobuzConnected = !!(() => { try { return localStorage.getItem('maestro_qobuz_user_id'); } catch { return null; } })();
  if (qobuzConnected) {
    defaults.qobuz.connected = true;
    if (!defaults.qobuz.active) defaults.qobuz.active = true;
  }
  const noAuthServices = ['local', 'live_radio', 'ella_radio', 'octave_radio'];
  for (const id of noAuthServices) {
    if (defaults[id].active) defaults[id].connected = true;
  }
  return defaults;
}

function loadInitialPrimary() {
  try {
    return localStorage.getItem('maestro_primary_service') || 'qobuz';
  } catch { return 'qobuz'; }
}

function loadInitialVoiceSearch() {
  try {
    const v = localStorage.getItem('maestro_voice_search');
    return v === null ? true : v === 'true';
  } catch { return true; }
}

export default function ServicesProvider({ children }) {
  const [services, setServices] = useState(loadInitialServices);
  const [primaryService, setPrimaryServiceState] = useState(loadInitialPrimary);
  const [voiceSearchEnabled, setVoiceSearchEnabledState] = useState(loadInitialVoiceSearch);
  const [activeModal, setActiveModal] = useState(null);
  const [serviceAuthForm, setServiceAuthForm] = useState({ username: '', password: '' });
  const servicesSaveTimer = useRef(null);
  const servicesRestoredRef = useRef(false);

  const setPrimaryService = useCallback((serviceId) => {
    setPrimaryServiceState(serviceId);
    try { localStorage.setItem('maestro_primary_service', serviceId); } catch {}
  }, []);

  const setVoiceSearchEnabled = useCallback((enabled) => {
    setVoiceSearchEnabledState(enabled);
    try { localStorage.setItem('maestro_voice_search', String(enabled)); } catch {}
  }, []);

  const getServicePriority = useCallback(() => {
    const result = [];
    if (services[primaryService]?.active) {
      result.push(primaryService);
    }
    for (const id of STREAMING_SERVICE_IDS) {
      if (id !== primaryService && services[id]?.connected) {
        result.push(id);
      }
    }
    if (services.local?.active) result.push('local');
    if (result.length === 0) result.push('qobuz');
    return result;
  }, [services, primaryService]);

  useEffect(() => {
    try {
      const toSave = { _defaults_version: DEFAULTS_VERSION };
      for (const [key, val] of Object.entries(services)) {
        toSave[key] = { active: val.active, connected: val.connected };
      }
      localStorage.setItem('maestro_services', JSON.stringify(toSave));
    } catch {}
  }, [services]);

  useEffect(() => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    if (servicesSaveTimer.current) clearTimeout(servicesSaveTimer.current);
    servicesSaveTimer.current = setTimeout(() => {
      const enabled = {};
      for (const [key, val] of Object.entries(services)) {
        enabled[key] = val.active === true;
      }
      enabled._primary = primaryService;
      enabled._voice_search = voiceSearchEnabled;
      fetch(`${CLOUD_API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled_services: enabled }),
      }).catch(() => {});
    }, 500);
    return () => { if (servicesSaveTimer.current) clearTimeout(servicesSaveTimer.current); };
  }, [services, primaryService, voiceSearchEnabled]);

  useEffect(() => {
    const unsub = useQobuzStore.subscribe((state) => {
      setServices((prev) => {
        const wasConnected = prev.qobuz?.connected === true;
        if (state.connected === wasConnected) return prev;
        return {
          ...prev,
          qobuz: { ...prev.qobuz, connected: state.connected, active: state.connected ? true : prev.qobuz.active },
        };
      });
    });
    return unsub;
  }, []);

  useEffect(() => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token || servicesRestoredRef.current) return;
    servicesRestoredRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/user/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const prefs = await res.json();
        if (prefs?.enabled_services && typeof prefs.enabled_services === 'object') {
          const es = prefs.enabled_services;
          if (es._primary) setPrimaryService(es._primary);
          if (es._voice_search !== undefined) setVoiceSearchEnabled(es._voice_search !== false);
          setServices((prev) => {
            const next = { ...prev };
            for (const [key, enabled] of Object.entries(es)) {
              if (key.startsWith('_')) continue;
              if (next[key]) {
                next[key] = { ...next[key], active: enabled === true };
                const noAuth = ['local', 'live_radio', 'octave_radio'];
                if (noAuth.includes(key)) next[key].connected = enabled === true;
              }
            }
            return next;
          });
        }
      } catch {}
    })();
  }, [setPrimaryService, setVoiceSearchEnabled]);

  useEffect(() => {
    if (!activeModal) return;
    setServiceAuthForm({ username: '', password: '' });
  }, [activeModal]);

  const value = useMemo(() => ({
    services,
    setServices,
    primaryService,
    setPrimaryService,
    voiceSearchEnabled,
    setVoiceSearchEnabled,
    getServicePriority,
    activeModal,
    setActiveModal,
    serviceAuthForm,
    setServiceAuthForm,
  }), [services, primaryService, voiceSearchEnabled, getServicePriority, activeModal, serviceAuthForm, setPrimaryService, setVoiceSearchEnabled]);

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}
